import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import {
  Button,
  DesignSystemSurface,
  EmptyState,
  PageHeader,
} from "../../../design-system";
import StoreUser from "../../../types/StoreUser";
import { BoardCard, isBoardOperator } from "../../../types/Board";
import {
  BoardCardInput,
  createBoardCard,
  moveBoardCard,
} from "../../../database/board/boardApi";
import useBoard from "./hooks/useBoard";
import IssueBoard from "./components/IssueBoard";
import CardFormModal from "./components/CardFormModal";
import CardDetailModal from "./components/CardDetailModal";
import StageEditorModal from "./components/StageEditorModal";

// The global Tribed issue board (/board). Reached from the toolbar icon, not
// the main nav strip. Every admin can view, add cards, comment, and attach;
// operators (isBoardOperator) can also drag cards between states, edit the
// state set, and delete cards.

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

type Props = {
  storeUser: StoreUser | null;
  community: any;
};

const BoardScreen: React.FC<Props> = ({ storeUser, community }) => {
  const { stages, cards, byStage, loading } = useBoard();
  const [showForm, setShowForm] = useState(false);
  const [showStages, setShowStages] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const operator = isBoardOperator(storeUser || undefined);

  const author = useMemo(
    () => ({
      uid: storeUser?.id || "",
      fullName:
        storeUser?.fullName ||
        [storeUser?.firstName, storeUser?.lastName].filter(Boolean).join(" ") ||
        storeUser?.email ||
        "Unknown",
      communityId: storeUser?.communityId || "",
    }),
    [storeUser]
  );

  // Keep the open detail modal live: re-resolve the card from the listener
  // data so edits, comments, and attachments appear without reopening.
  const selected: BoardCard | null = useMemo(
    () => cards.find((c) => c.id === selectedId) || null,
    [cards, selectedId]
  );

  const cardCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    cards.forEach((c) => (counts[c.status] = (counts[c.status] || 0) + 1));
    return counts;
  }, [cards]);

  const handleCreate = async (input: BoardCardInput) => {
    await createBoardCard(input, {
      ...author,
      communityName: community?.title || "",
    });
  };

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Board"
            subtitle="Bugs, feature requests, and improvements across Tribed"
            actions={
              <>
                {operator && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowStages(true)}
                  >
                    Edit states
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowForm(true)}
                >
                  Add card
                </Button>
              </>
            }
          />

          {!loading && cards.length === 0 ? (
            <EmptyState
              title="Nothing on the board yet"
              description="Add the first bug, feature request, or improvement."
              actions={
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowForm(true)}
                >
                  Add card
                </Button>
              }
            />
          ) : (
            <IssueBoard
              stages={stages}
              cards={cards}
              byStage={byStage}
              canMove={operator}
              onSelect={(card) => setSelectedId(card.id)}
              onMoveCard={(card, stage, position) =>
                moveBoardCard(card.id, stage, position)
              }
            />
          )}
        </PageInner>
      </Page>

      {showForm && (
        <CardFormModal
          onClose={() => setShowForm(false)}
          onCreate={handleCreate}
        />
      )}

      {selected && (
        <CardDetailModal
          card={selected}
          stages={stages}
          canEdit={operator}
          author={author}
          onClose={() => setSelectedId(null)}
        />
      )}

      {showStages && operator && (
        <StageEditorModal
          initial={stages}
          cardCounts={cardCounts}
          onClose={() => setShowStages(false)}
        />
      )}
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state: any) => ({
  storeUser: state.storeUser,
  community: state.community,
});

export default connect(mapStateToProps, null)(BoardScreen);
